<template lang="pug">
.w-full.h-full(ref="elRef")
  .w-full.h-full.flex.flex-col.min-h-0
    .iframe-bar.h-12.flex.flex-none.gap-3.w-full.justify-center.items-center.shadow-inner(
      v-if="props.showBar"
      :class="[width > 700 ? 'gap-2' : 'gap-1', loadFromInternal ? 'bg-yellow-300 dark_bg-yellow-600' : 'bg-stone-200 dark_bg-stone-700']"
    )

      //- UPDATE BUTTON
      button.btn.ripple.opacity-70.hover_opacity-100.dark_bg-opacity-0.bg-opacity-0.hover_bg-opacity-80.p-2.min-w-12(
        @click="update"
        aria-label="Update preview (crtl+S)"
        title="Update preview (crtl+S)"
      )
        Icon(icon="ph:arrows-left-right")
        span.text-sm.font-normal(:class="{ 'sr-only': width < 700 }") Update

      //- checkbox for live updates
      label.prose.prose-sm.dark_prose-invert.opacity-70.hover_opacity-100.dark_bg-opacity-0.bg-opacity-0.hover_bg-opacity-80.inline-flex.gap-1.items-center
        input(type="checkbox" :checked="liveUpdate" @change="liveUpdate = !liveUpdate")

        span(:class="{ 'text-xs': width < 700 }") Auto-update

      //- checkbox for including all tags
      label.prose.prose-sm.dark_prose-invert.opacity-70.hover_opacity-100.dark_bg-opacity-0.bg-opacity-0.hover_bg-opacity-80.inline-flex.gap-1.items-center(title="Include all tagged content")
        input(
          type="checkbox"
          :checked="includeAllTags"
          @change="includeAllTags = !includeAllTags"
        )

        span(:class="{ 'text-xs': width < 700 }") All tags

      label.prose.prose-sm.dark_prose-invert.opacity-70.hover_opacity-100.dark_bg-opacity-0.bg-opacity-0.hover_bg-opacity-80.inline-flex.gap-1.items-center(title="Enable inline editing in preview")
        input(
          type="checkbox"
          :checked="inlineEditEnabled"
          @change="inlineEditEnabled = !inlineEditEnabled"
        )

        span(:class="{ 'text-xs': width < 700 }") Editable

      //- checkbox for loading internal resources
      label.prose.prose-sm.dark_prose-invert.opacity-70.hover_opacity-100.dark_bg-opacity-0.bg-opacity-0.hover_bg-opacity-80.inline-flex.gap-1.items-center(title="Load internally published resources")
        input(
          type="checkbox"
          :checked="loadFromInternal"
          @change="loadFromInternal = !loadFromInternal"
        )

        span(:class="{ 'text-xs': width < 700 }") Internal

      //- word doc mode
      //- label.prose.prose-sm.dark_prose-invert.opacity-70.hover_opacity-100.dark_bg-opacity-0.bg-opacity-0.hover_bg-opacity-80.inline-flex.gap-1.items-center
        input(type="checkbox" :checked="wordMode" @change="wordMode = !wordMode")

        span(:class="{'text-xs': width < 700}") Word

      //- LOCATION BAR
      .iframe-location.rounded-md.bg-white.h-8.w-96.flex.items-center.mx-4
        span.ps-4.pe-1.opacity-50 #
        input.flex-1.h-full.p-4.ps-0.bg-transparent.font-monospace(
          :value="location"
          readonly="true"
          type="text"
        )

      //- ZOOM SLIDER
      span.relative.group.opacity-70.hover_opacity-100.dark_bg-opacity-0.bg-opacity-0.hover_bg-opacity-80(:class="{ 'hidden': width < 600 }")
        span.bg-black.bg-opacity-0.dark_bg-white.dark_bg-opacity-0.px-1.py-1.flex.hover_bg-opacity-5.rounded-md.h-10.items-center.text-gray-800.dark_text-gray-100.gap-2.px-2.group-hover_bg-opacity-5
          Icon.text-lg(:icon="zoom < 1 ? 'ph:magnifying-glass-minus' : 'ph:magnifying-glass-plus'" )
          span.text-sm(class="w-[4ch]") {{ Math.round(zoom * 100) }}%
        span.absolute.bg-white.bg-opacity-0.flex.items-center.justify-center.px-1.hidden.group-hover_block.z-100
          input.mt-2(type="range" v-model="zoom" min="0.4" max="2" step="0.2" class="w-32" orient="vertical")

      //- REFRESH BUTTON
      span
        button.btn.ripple.opacity-70.hover_opacity-100.dark_bg-opacity-0.bg-opacity-0.hover_bg-opacity-80.p-2(
          @click="refresh"
          aria-label="Refresh (F5)"
          title="Refresh (F5)"
        )
          Icon.text-lg(icon="ph:arrow-clockwise")
          span.text-sm.font-normal(:class="{ 'sr-only': width < 800 }") Reload

      //- RESET BUTTON
      span
        button.btn.ripple.opacity-70.hover_opacity-100.dark_bg-opacity-0.bg-opacity-0.hover_bg-opacity-80.p-2(
          @click="reset"
          aria-label="Reset (shift+F5)"
          title="Reset (shift+F5)"
        )
          Icon.text-lg(icon="ph:x-circle")
          span.text-sm.font-normal(:class="{ 'sr-only': width < 800 }") Reset



    ZoomableIframe#iframe.w-full.bg-stone-800.flex-1.min-h-0(
      ref="zoomableIframeRef"
      :zoom="zoom"
    )

    .absolute.h-auto.bottom-6.right-6.w-screen-xs.bg-red-200.border.text-red-800.p-6.rounded(v-if="yamlError")
      span.text-4xl.mb-2.-mt-2.inline-flex.items-center.justify-center.relative
        .animate-ping.absolute(class="inline-flex h-full w-full rounded-full bg-red-800/20")
        Icon(icon="ph:warning-diamond-duotone")
      pre.text-sm.font-mono.text-red-800 {{ yamlError }}

</template>

<script setup lang="ts">
import {
  ref,
  computed,
  watch,
  watchEffect,
  onMounted,
  onBeforeUnmount,
  nextTick,
  shallowRef,
  inject as vueInject
} from 'vue';
import type { Ref } from 'vue';
import type { MessageDialogState } from '../lib/useMessageDialog';
import { Icon } from '@iconify/vue';
import getJWT from '../../lib/getJWT';
import { getCourse, setLocation, setScroll } from '../store/courses';
import findTopWindow from '../../lib/findTopWindow';
import ZoomableIframe from './ZoomableIframe.vue';
import type { ComponentPublicInstance } from 'vue';
import Split from 'split.js';
import { useSafeLocalStorage } from '../../lib/safeLocalStorage';
import { load as yamlLoad, dump as yamlDump } from 'js-yaml';
import { useEventListener } from '@vueuse/core';
import { debounce } from 'lodash-es';
import getScreenById from '../lib/getScreenById';

const props = withDefaults(
  defineProps<{
    courseId: number;
    firmCode?: string;
    /** Path to app.html for preview iframe when on local server (e.g. ./app.html or ../app.html from /lite/) */
    previewAppPath?: string;
    /** Prefix for portal preview URL (e.g. /editor when lite is at portal…/editor/*). Omit for full author at authors.* */
    portalPreviewPrefix?: string;
    /** Show the toolbar (Update, Auto-update, Editable, etc.). False in lite author. */
    showBar?: boolean;
    /** Enable add-page controls inside the preview runtime. */
    addPageEnabled?: boolean;
  }>(),
  {
    previewAppPath: './app.html',
    portalPreviewPrefix: '',
    showBar: true,
    addPageEnabled: true
  }
);

const emit = defineEmits<{
  'current-screen': [screen: any];
  'compiled-yaml': [yaml: string];
}>();

const draft = shallowRef<CourseDraft>();
// const draft = computed(() => getCourse(props)?.draft);
const course = computed(() => getCourse(props));
type ZoomableIframeInstance = ComponentPublicInstance<typeof ZoomableIframe>;

const zoomableIframeRef = ref<ZoomableIframeInstance>();
const iframeRef = computed(() => zoomableIframeRef.value?.iframe);
const location = computed(() => course.value?.preview?.location);
const courseWindow = ref<CourseWindow>();
const zoom = useSafeLocalStorage<number>('uc-author-preview-zoom', 1);
const liveUpdate = ref(true);
const loadFromInternal = ref(false);
const includeAllTags = ref(false);
const inlineEditEnabled = ref(false);
const liteEditMode = vueInject<Ref<'view' | 'edit'> | undefined>(
  'liteEditMode',
  undefined
);
// Set while the agent streams or edits await review; forces inline editing off
// so user edits can't go stale against pending changes.
const inlineEditLocked = vueInject<Ref<boolean> | undefined>(
  'inlineEditLocked',
  undefined
);
const inlineEditLockReason = vueInject<
  Ref<'streaming' | 'review' | null> | undefined
>('inlineEditLockReason', undefined);
// Shared host dialog — opener/closer + state (state read only for auto-dismiss).
const openMessageDialog = vueInject<(cfg: Partial<MessageDialogState>) => void>(
  'openMessageDialog',
  () => {}
);
const closeMessageDialog = vueInject<() => void>(
  'closeMessageDialog',
  () => {}
);
const messageDialog = vueInject<MessageDialogState | undefined>(
  'messageDialog',
  undefined
);
const wordMode = ref(false);
const scormPlayerInitialized = ref(false);
const previewHeight = useSafeLocalStorage<number>(
  'uc-author-preview-height',
  100
);
const elRef = ref<HTMLElement>();

const width = ref(0);

const updateWidth = () => {
  if (!elRef.value) return;
  nextTick(() => (width.value = elRef.value?.offsetWidth || 0));
};

const debouncedUpdateWidth = debounce(updateWidth, 80, {
  leading: true,
  trailing: true
});
let resizeObserver: ResizeObserver | undefined;

watchEffect(updateWidth);
useEventListener('resize', debouncedUpdateWidth);

onMounted(() => {
  if (elRef.value) {
    resizeObserver = new ResizeObserver(() => {
      // Defer to next frame to avoid "ResizeObserver loop completed with undelivered notifications".
      requestAnimationFrame(() => debouncedUpdateWidth());
    });
    resizeObserver.observe(elRef.value);
    updateWidth();
  }

  if (!elRef.value || !props.showBar) return;

  // Keep split resizing only when toolbar is visible.
  Split(Array.from(elRef.value.children) as HTMLElement[], {
    direction: 'vertical',
    sizes: [previewHeight.value, 100 - previewHeight.value],
    gutterSize: 0,
    minSize: [30, 0],
    onDragEnd: sizes => {
      previewHeight.value = sizes[0];
    }
  });
});

const yamlError = ref('');

// Last YAML handed to updateYAML. An inline edit fires inject twice (explicit
// updatePreview() + the draft-save watch) with identical YAML; the second
// updateYAML would recompile the whole course for nothing. Reset on refresh().
let lastInjectedYaml = '';

const inject = (d = draft.value) => {
  console.log('[PREVIEW inject]', performance.now().toFixed(0), {
    hasDraft: !!d,
    hasCourseWindow: !!courseWindow.value,
    injectYamlLen: d?.yaml?.length,
    injectUpdated: d?.metadata?.updatedDate,
    willInject: !!d && !!courseWindow.value
  });
  if (!d) return;
  if (!courseWindow.value) return;
  const { injectCss, updateYAML } = courseWindow.value;
  const { css, yaml, compiledCss, type } = d;

  // inject css
  if (compiledCss && injectCss) injectCss(compiledCss);
  else if (css && injectCss) injectCss(css);

  // inject yaml, if valid
  try {
    yamlLoad(yaml || '');
    yamlError.value = '';
  } catch (e) {
    yamlError.value = e.message;
    return;
  }

  console.log('[AuthorPreview inject] About to call updateYAML with YAML:', {
    draftType: type,
    hasPatches: yaml?.includes('patches:'),
    yamlPreview: yaml?.slice(0, 500)
  });

  onBeforeUnmount(() => {
    resizeObserver?.disconnect();
  });

  if (yaml && updateYAML && yaml !== lastInjectedYaml) {
    lastInjectedYaml = yaml;
    // Send YAML to the appropriate slot based on draft type
    if (type === 'course') {
      // Editing a base course - update base content
      updateYAML({ baseYaml: yaml });
    } else {
      // Editing a customization - update custom content (legacy behavior)
      updateYAML({ customYaml: yaml });
    }

    setTimeout(() => {
      const store = (courseWindow.value as any)?.store;
      console.log(
        '[AuthorPreview inject] After updateYAML, runtime patches:',
        store?.compiled?.patches,
        'runtime previes:',
        store?.compiled?.previews
      );
    }, 100);
  }
};

const editorWindow = findTopWindow({
  withOpeners: true,
  predicate: w => 'syncCourseToRef' in w
}) as EditorWindow;

const stopCourseSync = editorWindow.syncCourseToRef(draft);

// Live preview recompile is heavy: the runtime rebuilds the whole compiled
// course (cloneDeep ~250KB + re-apply every patch) on each updateYAML, blocking
// the main thread for a beat. While a key is held the draft fires every throttle
// tick, so debounce: recompile on the leading edge and after typing settles,
// with maxWait keeping it refreshing on long holds. TipTap renders characters
// locally, so the preview lagging a beat is invisible. Manual refresh paths
// (refresh/goToSection) reload the iframe directly and are unaffected.
const liveInject = debounce(() => inject(), 250, {
  leading: true,
  trailing: true,
  maxWait: 600
});
onBeforeUnmount(() => {
  stopCourseSync?.();
  liveInject.cancel();
});

watch(draft, () => {
  if (liveUpdate.value) liveInject();
});

const update = () => {
  liveInject();
};

watch(liveUpdate, v => {
  if (v) inject();
});

const applyInlineEditToggle = (enabled: boolean) => {
  try {
    courseWindow.value?.setAuthorInlineEdit?.(enabled);
  } catch (e) {
    console.error('setAuthorInlineEdit failed:', e);
  }
};

const applyAddPageToggle = (enabled: boolean) => {
  try {
    courseWindow.value?.setAuthorAddPageEnabled?.(enabled);
  } catch (e) {
    console.error('setAuthorAddPageEnabled failed:', e);
  }
};

const applyInlineEditLock = (locked: boolean) => {
  try {
    courseWindow.value?.setAuthorInlineEditLocked?.(locked);
  } catch (e) {
    console.error('setAuthorInlineEditLocked failed:', e);
  }
};

// Whether edit mode should be on. In Lite the host drives it via liteEditMode;
// in full author it's the toolbar checkbox.
const inlineEditDesired = computed(() =>
  liteEditMode ? liteEditMode.value === 'edit' : inlineEditEnabled.value
);

watch(inlineEditDesired, applyInlineEditToggle);

// While locked (agent streaming or edits pending review), suppress inline
// editing without leaving edit mode — keeps section unlocking + edit-mode
// visuals intact while preventing edits that would go stale against pending changes.
watch(() => Boolean(inlineEditLocked?.value), applyInlineEditLock);

const blockedMessageForReason = (
  reason: 'streaming' | 'review' | null | undefined
): string => {
  if (reason === 'streaming') {
    return 'The assistant is still applying changes. You can edit again once it has finished and any changes resolved.';
  }
  if (reason === 'review') {
    return 'You have changes to review. Accept or reject them before editing.';
  }
  return 'Editing is paused right now. Please try again in a moment.';
};

// The runtime fires uc:inlineEditBlocked when the user triggers an edit on a
// locked editor; explain why via the shared host dialog.
const openInlineEditBlockedDialog = () => {
  openMessageDialog({
    id: 'inline-edit-blocked',
    title: 'Editing paused',
    message: blockedMessageForReason(inlineEditLockReason?.value),
    actions: [
      { label: 'Got it', primary: true, onClick: () => closeMessageDialog() }
    ]
  });
};

// Auto-dismiss the "editing paused" dialog once editing is allowed again, so a
// late-dismissed dialog doesn't linger over a now-editable preview.
watch(
  () => Boolean(inlineEditLocked?.value),
  locked => {
    if (!locked && messageDialog?.id === 'inline-edit-blocked')
      closeMessageDialog();
  }
);

watch(
  () => props.addPageEnabled,
  value => {
    applyAddPageToggle(Boolean(value));
  }
);

const initScormPlayer = () => {
  const { firstName, lastName, email } = getJWT().userProfile;

  const cmi = {
    'cmi.suspend_data':
      `Var_firm_name=${props.firmCode || '___'};` +
      `Var_courseid=${props.courseId};`,
    'cmi.core.student_name': `${lastName}, ${firstName}`,
    'cmi.core.student_id': email,
    'cmi.core.lesson_location': location.value || undefined
  };

  // SCORM 1.2 API
  window.API = {
    LMSInitialize: () => 'true',
    LMSFinish: () => 'true',
    LMSGetDiagnostic: () => {},
    LMSGetLastError: () => 0,
    LMSGetValue: (k: string) => cmi[k],

    LMSSetValue(k: string, v: string) {
      cmi[k] = v;

      if (k === 'cmi.core.lesson_location') {
        setLocation(props, v);
      }

      return 'true';
    },

    LMSCommit() {
      return 'true';
    },

    LMSGetErrorString(code: string) {
      return {
        0: 'No error',
        101: 'General Exception',
        201: 'Invalid argument error',
        202: 'Element cannot have children',
        203: 'Element not an array. Cannot have count.',
        301: 'Not initialized',
        401: 'Not implemented error',
        402: 'Invalid set value, element is a keyword',
        403: 'Element is read only.',
        404: 'Element is write only',
        405: 'Incorrect Data Type'
      }[code];
    }
  };
};

const updateScroll = (s: number) => {
  const container = courseWindow.value?.document?.querySelector('.scroller');
  if (container) {
    container.scrollTo(0, s);
  }
};

// watch(() => course.value?.preview?.scroll, updateScroll);

const iframeSource = computed(() => {
  // Original UberCourse logic
  const localServer = window.location.origin.includes('local');
  const portalOrigin = new URL(window.location.href).origin;
  // pass tags from the editor window URL to the preview iframe
  const tagsParam =
    new URLSearchParams(editorWindow.location.search).get('tags') || '';

  // reset the local suspend_data stored tags value
  if (window.API && !includeAllTags.value) window.API.LMSSetValue('tags', '');

  const params = [
    `courseid=${props.courseId}`,
    props.firmCode && `firm=${props.firmCode}`,
    // unpublished customizations aren't on S3 yet; tell the preview runtime to
    // load the latest draft/saved from mod-content (priority over S3).
    props.firmCode && 'previewDraft=1',
    wordMode.value && 'word=1',
    // wordMode.value && 'audioDoc=1', // include the audio file names in the transcript
    wordMode.value && 'tags=word+includeall',
    !wordMode.value &&
      !includeAllTags.value &&
      tagsParam &&
      `tags=${tagsParam}`,
    !wordMode.value && includeAllTags.value && 'tags=includeall',
    loadFromInternal.value && 'internal=true'
  ].filter(Boolean);

  const query = params.length ? '?' + params.join('&') : '';

  console.log(query);

  const url = localServer
    ? `${props.previewAppPath}${query}`
    : `${portalOrigin}${props.portalPreviewPrefix}/latest/app.html${query}`;

  return url;
});

watch(
  () => iframeSource.value,
  () => {
    const iframe = iframeRef.value as HTMLIFrameElement | undefined;
    if (!iframe) return;
    emit('current-screen', null);
    courseWindow.value = undefined;
    iframe.src = iframeSource.value;
  }
);

// Compiled YAML coming from Pinia store inside the iframe's window
const compiledYamlString = ref<string>('');
const compiledObj = ref<any>({});

// Serializing the full compiled course (can be ~250KB) is expensive and only
// feeds the agent buffer (compiledYamlRef -> AuthorEditAgentWrapper), not the
// live preview render. The runtime fires uc:compiled-updated several times per
// recompile, so debounce to one trailing dump after typing settles. The
// compiledYamlString watcher re-emits 'compiled-yaml' reactively.
const serializeCompiledDebounced = debounce(
  (base: any) => {
    compiledYamlString.value = yamlDump(base);
    console.log('[PREVIEW compiled->up]', performance.now().toFixed(0), {
      compiledYamlLen: compiledYamlString.value.length,
      injectedDraftYamlLen: draft.value?.yaml?.length,
      injectedDraftUpdated: draft.value?.metadata?.updatedDate
    });
  },
  500,
  { trailing: true }
);

watchEffect(async function initCourseWindow() {
  if (!draft.value) return;
  if (courseWindow.value) return;

  if (!scormPlayerInitialized.value) {
    initScormPlayer();
    scormPlayerInitialized.value = true;
  }

  editorWindow.setLatestScroll = (s: number) => {
    setScroll(props, s);
  };

  const iframe = iframeRef.value as HTMLIFrameElement | undefined;
  if (!iframe) return;

  // Fresh iframe/runtime: the inject() guard must not skip the first push.
  lastInjectedYaml = '';

  await new Promise<void>(resolve => {
    iframe.addEventListener('load', () => {
      resolve();
    });

    iframe.src = iframeSource.value;
  });

  const cw = iframe.contentWindow as CourseWindow;

  courseWindow.value = cw;

  applyInlineEditToggle(inlineEditDesired.value);
  applyInlineEditLock(Boolean(inlineEditLocked?.value));
  applyAddPageToggle(Boolean(props.addPageEnabled));

  // Runtime fires this when the user tries to edit while inline editing is locked.
  cw.addEventListener('uc:inlineEditBlocked', openInlineEditBlockedDialog);

  // Re-apply initial inline edit toggle once the iframe exposes hooks
  cw.addEventListener('uc:inlineedithooksready', () => {
    applyInlineEditToggle(inlineEditDesired.value);
    applyInlineEditLock(Boolean(inlineEditLocked?.value));
    applyAddPageToggle(Boolean(props.addPageEnabled));
  });

  let poll;

  // Helper to pull compiled from iframe store
  const syncCompiled = () => {
    const store = (cw as any)?.store;
    const obj = store?.compiled;
    if (!(obj && 'pages' in obj)) return;

    compiledObj.value = obj;
    // Agent buffer uses the committed compiled (no uncommitted previews) so
    // re-applying a preview onto it stays idempotent; fall back to the full
    // compiled if an older runtime lacks compiledNoPreviews. Debounced: the
    // dump is heavy and only the agent reads it. compiledYamlString's watcher
    // emits 'compiled-yaml' once the trailing dump lands.
    const base = store?.compiledNoPreviews ?? obj;
    serializeCompiledDebounced(base as any);
    clearInterval(poll);
    return obj;
  };

  poll = setInterval(() => {
    if (syncCompiled()) clearInterval(poll);
  }, 500);
  cw.addEventListener('uc:readytoinject', syncCompiled);
  // Re-sync after every recompile (accept/commit/inline edit), not just at boot —
  // otherwise compiledYamlString stays frozen at the load-time course and the
  // agent matches oldText against a stale buffer.
  cw.addEventListener('uc:compiled-updated', syncCompiled);

  cw.addEventListener('uc:scrollpagemounted', e => {
    updateScroll(course.value.preview.scroll);

    // Get current section and emit screen info
    const currentSection = cw.document.querySelector('.section--active');
    if (currentSection) {
      const screenId = currentSection.id;
      // Get screen object from compiled YAML
      try {
        const screen = getScreenById(screenId, compiledObj.value);
        if (screen) {
          console.log('AuthorPreview emit current-screen', screen);
          emit('current-screen', screen);
        }
      } catch (e) {
        console.warn('Failed to lookup screen from compiled YAML:', e);
      }
    }
  });

  // Listen for section changes
  cw.addEventListener('scrollsectionentered', e => {
    const currentSection = e.target as HTMLElement;
    if (currentSection) {
      const screenId = currentSection.id;
      try {
        const screen = getScreenById(screenId, compiledObj.value);
        if (screen) {
          console.log('AuthorPreview emit current-screen', screen);
          emit('current-screen', screen);
        }
      } catch (e) {
        console.warn('Failed to lookup screen from compiled YAML:', e);
      }
    }
  });
  // Expose compiled YAML getter for the editor
  cw.getCompiledYaml = () => compiledYamlString.value;

  courseWindow.value = cw;
  inject(); // backend load may have updated draft.value before the iframe was ready
});

// Re-emit whenever compiled YAML changes (e.g. after AI patch accepted)
watch(
  compiledYamlString,
  v => {
    if (v) emit('compiled-yaml', v);
  },
  { immediate: true }
);

/**
 * Refresh the iframe. If hard refresh, also reset SCORM data and scroll position.
 */
const refresh = async () => {
  const iframe = iframeRef.value as HTMLIFrameElement;
  const url = new URL(iframe.src);

  url.hash = '/' + location.value;

  iframe.src = '';

  emit('current-screen', null);
  courseWindow.value = undefined;
  // New iframe = fresh runtime; force the next inject to push YAML again.
  lastInjectedYaml = '';
  // if (hard) {
  //   window.latestScroll = 0;
  //   initScormPlayer();
  // }

  setTimeout(() => {
    iframe.src = url.href;
  }, 100);
};

const isInlineEditorActive = () =>
  Boolean(courseWindow.value?.__authorInlineEditorActive);

const finalizeInlineEditSession = async () => {
  const cw = courseWindow.value;
  if (!cw) return;

  const activeEl = cw.document.activeElement as HTMLElement | null;
  activeEl?.blur?.();

  await new Promise(resolve => setTimeout(resolve, 120));
};

const reset = () => {
  scormPlayerInitialized.value = false;
  initScormPlayer();
  courseWindow.value?.clearSuspendData();
};

// Preview patch methods for AI edit agent
const emitAuthorPreviewDiffUpdated = (screenId: string) => {
  const cw = courseWindow.value;
  if (!cw) return;
  try {
    cw.dispatchEvent(
      new CustomEvent('uc:author-preview-diff-updated', {
        detail: { screenId }
      })
    );
  } catch (e) {
    console.warn('[emitAuthorPreviewDiffUpdated] dispatch failed:', e);
  }
};

const previewPatch = (screenYaml: string) => {
  try {
    const parsed = yamlLoad(screenYaml) as any;
    if (!parsed?.id) {
      console.warn('[previewPatch] Screen YAML missing id');
      return;
    }

    const store = courseWindow.value?.store;
    if (!store?.edits) {
      console.warn('[previewPatch] Store or edits not available');
      return;
    }

    const cw = courseWindow.value;
    const compiled = cw ? (cw as any).store?.compiled : undefined;
    const originalOnScreen =
      compiled && parsed.id ? getScreenById(parsed.id, compiled) : null;

    if (cw) {
      cw.__authorPreviewDiffs = cw.__authorPreviewDiffs || {};
      cw.__authorPreviewDiffs[parsed.id] = {
        originalScreen: originalOnScreen
          ? structuredClone(originalOnScreen)
          : null,
        previewScreen: structuredClone(parsed)
      };
    }

    // Set the preview in the store's edits (previews get merged with patches)
    // Note: patches/previews format is { screenId: [screenObject] } - array containing the screen
    if (!store.edits.previews) {
      store.edits.previews = {};
    }
    store.edits.previews[parsed.id] = [parsed];

    emitAuthorPreviewDiffUpdated(parsed.id);
  } catch (e) {
    console.error('[previewPatch] Failed to parse screen YAML:', e);
  }
};

// keepDiff: unapply the preview (so the runtime renders the pre-edit text) but
// retain the diff snapshot. Used by "Show previous version" so the inline word
// diff can highlight the words that will be removed.
const clearPreviewPatch = (screenId: string, keepDiff = false) => {
  try {
    const store = courseWindow.value?.store;
    if (store?.edits?.previews && screenId in store.edits.previews) {
      delete store.edits.previews[screenId];
    }

    const cw = courseWindow.value;
    if (
      !keepDiff &&
      cw?.__authorPreviewDiffs &&
      screenId in cw.__authorPreviewDiffs
    ) {
      delete cw.__authorPreviewDiffs[screenId];
    }
    emitAuthorPreviewDiffUpdated(screenId);
  } catch (e) {
    console.error('[clearPreviewPatch] Failed to clear preview:', e);
  }
};

defineExpose({
  update,
  refresh,
  isInlineEditorActive,
  finalizeInlineEditSession,
  reset,
  courseWindow,
  previewPatch,
  clearPreviewPatch,
  goToSection: async (pageId: string, screenId: string) => {
    const cw = courseWindow.value;

    const unlockPathToSection = async () => {
      if (!cw) return;
      const scorm = (cw as any).SCORM;
      if (!scorm?.setCompleted || !compiledObj.value?.pages) return;

      const pages: any[] = compiledObj.value.pages;
      const targetPageIndex = pages.findIndex(p => p?.id === pageId);
      if (targetPageIndex === -1) return;

      // Unlock linear navigation up to target page.
      for (let i = 0; i < targetPageIndex; i++) {
        const prevPageId = pages[i]?.id;
        if (prevPageId) scorm.setCompleted(prevPageId);
      }

      // Unlock sections up to target section on target page.
      const targetPage = pages[targetPageIndex];
      const targetScreens: any[] = Array.isArray(targetPage?.screens)
        ? targetPage.screens
        : [];
      const targetScreenIndex = targetScreens.findIndex(
        s => s?.id === screenId
      );
      if (targetScreenIndex !== -1) {
        for (let i = 0; i < targetScreenIndex; i++) {
          const prevScreenId = targetScreens[i]?.id;
          if (prevScreenId) scorm.setCompleted(prevScreenId);
        }
      }

      const pageEl = cw.document?.getElementById(pageId) as any;
      await pageEl?.refreshSections?.();
    };

    if (cw && typeof cw.goToSection === 'function') {
      await unlockPathToSection();
      await cw.goToSection(pageId, screenId);
      return;
    }

    // Fallback: simple DOM scroll
    if (cw && cw.document) {
      // Try finding screen element by id
      const target = cw.document.getElementById(screenId);
      if (target && typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }

    console.warn(
      'goToSection method not available on courseWindow and fallback failed'
    );
  }
});
</script>
